import React from 'react';
import { Button, IconButton, Stack, Container, Box, Typography, MenuItem, ListItemText, Divider } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { matchPath, NavLink, useLocation, useNavigate } from 'react-router-dom';
import logo from 'assets/logo.svg';
import { HeaderWrapper, StyledMenu } from './styled';
import { RouteList } from 'types';
import { config } from 'config';
import { UserAccount } from './UserAccount';
import { useIsMobile } from 'hooks/useIsMobile';
import { DiscountBadge } from 'components/DiscountBadge';
import { isDiscountActive } from 'utils';

export const PageHeader = () => {
  const { pathname } = useLocation();
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();

  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const shouldShowAllButtons = !matchPath({ path: RouteList.Ga4Config }, pathname);
  const showDiscountActive = isDiscountActive();

  return (
    <HeaderWrapper>
      <Container>
        <Stack justifyContent="space-between" alignItems="center" direction="row" sx={{ pt: 4, pb: 4 }}>
          <img src={logo} alt="Alloy ads logo" height={isMobile ? 36 : 40} />
          <Stack direction="row">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              sx={{ mr: 2, display: { sm: 'none' } }}
              onClick={handleMobileMenuOpen}
            >
              <MenuIcon />
            </IconButton>
            {/* Navigation Links */}
            <Box sx={{ display: { xs: 'none', sm: 'block' }, mr: shouldShowAllButtons ? 10 : 0 }}>
              <Stack direction="row" spacing={4}>
                <Button
                  component="a"
                  href={config.alloyPublicUrl}
                  target="_blank"
                  color="inherit"
                  sx={{ textTransform: 'none', p: 3 }}
                >
                  <Typography variant="body2">Über Alloy</Typography>
                </Button>

                {shouldShowAllButtons && (
                  <Button
                    component={NavLink}
                    to={RouteList.MyCampaigns}
                    end
                    color="inherit"
                    sx={({ palette }) => ({
                      textTransform: 'none',
                      '&.active': {
                        background: palette.rose[50],
                        color: palette.rose[500],
                      },
                      p: 3,
                    })}
                  >
                    <Typography variant="body2">Meine Kampagnen</Typography>
                  </Button>
                )}

                {shouldShowAllButtons && (
                  <Box position="relative">
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => navigate(RouteList.CampaignCreate)}
                      sx={{
                        textTransform: 'none',
                        border: '1px solid #BB448B',
                        color: '#BB448B',
                        p: '12px 16px',
                        '&:hover': {
                          backgroundColor: 'rgba(187, 68, 139, 0.04)',
                        },
                      }}
                    >
                      <Typography variant="body2" textTransform="uppercase">
                        Kampagne erstellen
                      </Typography>
                    </Button>
                    {showDiscountActive ? (
                      <DiscountBadge
                        sx={{
                          position: 'absolute',
                          bottom: -14,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          height: '26px',
                          '& .MuiChip-label': {
                            px: '12px',
                            py: '4px',
                          },
                        }}
                      />
                    ) : null}
                  </Box>
                )}
              </Stack>
            </Box>
            {shouldShowAllButtons && <UserAccount />}
          </Stack>
        </Stack>

        {/* Mobile Menu */}
        <StyledMenu
          open={isMobileMenuOpen && isMobile}
          anchorEl={mobileMenuAnchorEl}
          onClose={handleMobileMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem component="a" href={config.alloyPublicUrl} target="_blank" onClick={handleMobileMenuClose}>
            <ListItemText>Über Alloy</ListItemText>
          </MenuItem>
          {shouldShowAllButtons && (
            <MenuItem component={NavLink} to={RouteList.MyCampaigns} end onClick={handleMobileMenuClose}>
              <ListItemText>Meine Kampagnen</ListItemText>
            </MenuItem>
          )}
          {shouldShowAllButtons && (
            <MenuItem
              onClick={() => {
                navigate(RouteList.CampaignCreate);
                handleMobileMenuClose();
              }}
            >
              <ListItemText>Kampagne erstellen</ListItemText>
              {showDiscountActive ? (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 2, border: 0 }} />
                  <DiscountBadge />
                </>
              ) : null}
            </MenuItem>
          )}
        </StyledMenu>
      </Container>
    </HeaderWrapper>
  );
};
